<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import { formatWaitingTime } from '../utils/reportFormatters';
import {
  QUESTION_TYPE_LABELS,
  SUPPORT_ORIGIN_LABELS,
  dutyRegionLabel,
  dutySupportQuestionLabel,
} from '../utils/dutySupportTemplates';

// Lista "Aguardando" do plantão: cada conversa pendente com taxonomia TP/CX,
// tempo de espera e ações de atendimento/Captain.
defineProps({
  conversations: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  hasItems: { type: Boolean, default: false },
  claimingId: { type: [Number, String], default: null },
  captainSuggestions: { type: Object, default: () => ({}) },
  captainLoadingId: { type: [Number, String], default: null },
});

const emit = defineEmits([
  'claim',
  'open',
  'copy',
  'suggest',
  'copy-suggestion',
]);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisCard title="Aguardando">
    <div v-if="loading && !hasItems" class="py-12 text-center">
      <span
        class="i-lucide-loader-circle mx-auto block size-8 animate-spin text-lais-primary"
        aria-hidden="true"
      />
    </div>
    <div v-else-if="conversations.length" class="grid gap-3">
      <article
        v-for="conversation in conversations"
        :key="conversation.id"
        class="rounded-lais border border-lais-border-soft bg-white p-4"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <p class="truncate text-[15px] font-semibold text-lais-text">
              {{ conversation.contact?.name || 'Cliente sem nome' }}
            </p>
            <p class="mt-1 truncate text-[12px] text-lais-text-muted">
              {{ conversation.inbox?.name || 'Inbox' }}
            </p>
            <div
              v-if="
                conversation.duty?.support_origin ||
                conversation.duty?.question_type
              "
              class="mt-2 flex flex-wrap items-center gap-1.5"
            >
              <span
                v-if="conversation.duty?.support_origin"
                class="rounded-full px-2 py-0.5 text-[11px] font-semibold"
                :class="
                  conversation.duty.support_origin === 'tenant_prospect'
                    ? 'bg-lais-gold/15 text-lais-gold'
                    : 'bg-lais-primary/10 text-lais-primary'
                "
              >
                {{ SUPPORT_ORIGIN_LABELS[conversation.duty.support_origin] }}
              </span>
              <span
                v-if="QUESTION_TYPE_LABELS[conversation.duty?.question_type]"
                class="rounded-full bg-lais-border-soft px-2 py-0.5 text-[11px] font-medium text-lais-text-muted"
              >
                {{ QUESTION_TYPE_LABELS[conversation.duty.question_type] }}
              </span>
              <span
                v-if="
                  conversation.duty?.business_hours === 'outside_business_hours'
                "
                class="rounded-full bg-lais-ruby/10 px-2 py-0.5 text-[11px] font-semibold text-lais-ruby"
              >
                Fora do horário
              </span>
              <span
                v-if="dutyRegionLabel(conversation)"
                class="rounded-full bg-lais-teal/10 px-2 py-0.5 text-[11px] font-semibold text-lais-teal"
              >
                {{ dutyRegionLabel(conversation) }}
              </span>
              <span
                v-if="dutySupportQuestionLabel(conversation)"
                class="max-w-full truncate rounded-full bg-lais-primary-50 px-2 py-0.5 text-[11px] font-medium text-lais-primary"
              >
                {{ dutySupportQuestionLabel(conversation) }}
              </span>
            </div>
          </div>
          <span
            v-if="conversation.duty?.status === 'transferred'"
            class="flex items-center gap-1 rounded-full bg-lais-border-soft px-3 py-1 text-[12px] font-semibold text-lais-text-muted"
          >
            <span class="i-lucide-arrow-right-left" />
            Transferido
          </span>
          <span
            v-else
            class="rounded-full bg-lais-gold/15 px-3 py-1 text-[12px] font-semibold text-lais-gold"
          >
            {{ formatWaitingTime(conversation.duty?.waiting_seconds) }}
          </span>
        </div>
        <div class="mt-4 flex flex-wrap gap-2">
          <LaisPrimaryButton
            v-if="conversation.duty?.status !== 'transferred'"
            icon="i-lucide-play"
            size="sm"
            :is-loading="claimingId === conversation.id"
            @click="emit('claim', conversation)"
          >
            Iniciar atendimento
          </LaisPrimaryButton>
          <LaisPrimaryButton
            variant="ghost"
            icon="i-lucide-external-link"
            size="sm"
            @click="emit('open', conversation)"
          >
            Abrir conversa
          </LaisPrimaryButton>
          <LaisPrimaryButton
            variant="ghost"
            icon="i-lucide-copy"
            size="sm"
            @click="emit('copy', conversation)"
          >
            Copiar resposta
          </LaisPrimaryButton>
          <LaisPrimaryButton
            variant="ghost"
            icon="i-lucide-sparkles"
            size="sm"
            :is-loading="captainLoadingId === conversation.id"
            @click="emit('suggest', conversation)"
          >
            Sugerir resposta
          </LaisPrimaryButton>
        </div>
        <div
          v-if="captainSuggestions[conversation.id]"
          class="mt-4 rounded-lais border border-lais-primary/20 bg-lais-primary-50/40 p-3"
        >
          <p class="text-[12px] font-semibold text-lais-primary">Captain</p>
          <p class="mt-2 text-[13px] leading-relaxed text-lais-text-body">
            {{ captainSuggestions[conversation.id] }}
          </p>
          <LaisPrimaryButton
            class="mt-3"
            variant="ghost"
            icon="i-lucide-copy"
            size="sm"
            @click="emit('copy-suggestion', conversation)"
          >
            Copiar sugestão
          </LaisPrimaryButton>
        </div>
      </article>
    </div>
    <div
      v-else
      class="grid min-h-[180px] place-items-center px-4 py-8 text-center"
    >
      <div>
        <span
          class="i-lucide-info mx-auto block size-8 text-lais-text-muted"
          aria-hidden="true"
        />
        <p class="mt-3 text-[14px] font-semibold text-lais-text">
          Nenhum cliente aguardando no plantão
        </p>
        <p class="mt-1 text-[12px] text-lais-text-muted">
          Novas conversas aparecem aqui com o tempo de espera.
        </p>
      </div>
    </div>
  </LaisCard>
</template>
